import Link from "next/link";
import { FC, useMemo } from "react";
import { FavoriteItemResponse } from "@/types/FavoriteItemResponse";
import Avatar, { AvatarSize } from "@/components/Avatar";
import LinkAsModal from "@/components/LinkAsModal";
import useListChildItem from "@/features/list/hooks/useListChildItem";
import Maybe from "@/components/Maybe";
import { calculateOptimalListChildRatio } from "@/utils/calculateOptimalListChildRatio";

export const ListChildItem: FC<{
  listItem: FavoriteItemResponse;
}> = ({ listItem }) => {
  const {
    entityId,
    entityType,
    itemHref,
    authorHref,
    authorIconSrc,
    type,
    params,
  } = useListChildItem(listItem);
  const { upload_image: itemImage } = listItem;

  const aspectRatio = useMemo(
    () => calculateOptimalListChildRatio(itemImage.width, itemImage.height),
    [itemImage.width, itemImage.height],
  );

  return (
    <article className="space-y-8">
      <LinkAsModal
        entityId={entityId}
        entityType={entityType}
        as={itemHref}
        params={params}
      >
        <a>
          <span
            className="bg-contain bg-center bg-no-repeat rounded-md w-full block bg-white"
            style={{
              aspectRatio,
              backgroundImage: `url(${itemImage.urls.small})`,
            }}
          />
        </a>
      </LinkAsModal>

      <div className="flex gap-8">
        {authorIconSrc && (
          <>
            {authorHref ? (
              <Link href={authorHref} className="flex-shrink-0">
                <Avatar
                  size={AvatarSize.Small}
                  src={authorIconSrc}
                  alt={listItem.author.name}
                />
              </Link>
            ) : (
              <Avatar
                size={AvatarSize.Small}
                src={authorIconSrc}
                alt={listItem.author.name}
              />
            )}
          </>
        )}

        <div className="flex-1">
          {listItem.name && (
            <LinkAsModal
              entityId={entityId}
              entityType={entityType}
              as={itemHref}
              params={params}
            >
              <a className="block text-xs hover:underline">{listItem.name}</a>
            </LinkAsModal>
          )}
          <div className="text-2xs text-secondary">
            <span>
              {type}

              <Maybe condition={!!authorHref}>
                <span>&nbsp;/&nbsp;</span>
                <Link
                  href={authorHref}
                  prefetch={false}
                  className="hover:underline"
                >
                  {listItem.author.name}
                </Link>
              </Maybe>
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ListChildItem;
